import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { PageHeader } from '../components/Form';
import { EmptyState, StatusPill } from '../components/PlayerUI';
import { SkeletonOrderRow } from '../components/Skeleton';
import { useTheme } from '../context/ThemeContext';

type Filter = 'all' | 'funds' | 'games' | 'cashouts';
type Order = {
  id: string;
  amount: number | string;
  status: string;
  createdAt: string;
  type: 'topup' | 'withdraw';
  target?: 'wallet' | 'game';
  source?: 'wallet' | 'game';
  game?: { name: string } | null;
  paymentMethod?: { name: string } | null;
  payoutTag?: string;
};

function statusKind(status: string): 'processing' | 'completed' | 'rejected' {
  if (['Completed', 'Paid', 'Verified'].includes(status)) return 'completed';
  if (['Rejected', 'Cancelled'].includes(status)) return 'rejected';
  return 'processing';
}

export default function Transactions() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get<Omit<Order, 'type'>[]>('/orders/topup/my'), api.get<Omit<Order, 'type'>[]>('/orders/withdraw/my')])
      .then(([topUps, withdrawals]) => setOrders([
        ...topUps.data.map(order => ({ ...order, type: 'topup' as const })),
        ...withdrawals.data.map(order => ({ ...order, type: 'withdraw' as const })),
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'cashouts') return order.type === 'withdraw';
    if (filter === 'funds') return order.type === 'topup' && order.target === 'wallet';
    return order.type === 'topup' && order.target !== 'wallet';
  }), [orders, filter]);

  const groups = useMemo(() => filtered.reduce<Record<string, Order[]>>((result, order) => {
    const month = new Date(order.createdAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    (result[month] ||= []).push(order);
    return result;
  }, {}), [filtered]);

  const describe = (order: Order) => {
    if (order.type === 'withdraw') return { sign: '−', title: order.paymentMethod?.name || order.payoutTag || t('profile.transactions.cashOut'), subtitle: order.source === 'game' ? t('profile.transactions.fromGame') : t('profile.transactions.fromBalance') };
    if (order.target === 'wallet') return { sign: '+', title: order.paymentMethod?.name || t('profile.transactions.slSweep'), subtitle: t('profile.transactions.addedFunds') };
    return { sign: '−', title: order.game?.name || t('profile.transactions.game'), subtitle: t('profile.transactions.gameLoad') };
  };

  return (
    <main className="sls-page">
      <PageHeader title={t('profile.transactions.title')} />
      <div className="sls-page-content">
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {(['all', 'funds', 'games', 'cashouts'] as Filter[]).map(option => (
            <button key={option} onClick={() => setFilter(option)} style={{ minHeight: 48, padding: '0 14px', borderRadius: 12, border: `1px solid ${filter === option ? colors.accent : colors.line}`, background: filter === option ? colors.accentSoft : colors.surface, color: colors.text, fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap' }}>{t(`profile.transactions.filters.${option}`)}</button>
          ))}
        </div>

        {loading ? <div style={{ marginTop: 20 }}><SkeletonOrderRow /><SkeletonOrderRow /><SkeletonOrderRow /></div> : Object.keys(groups).length ? Object.entries(groups).map(([month, items]) => (
          <section key={month}>
            <h2 style={{ margin: '28px 0 8px', fontSize: 20, fontWeight: 700 }}>{month}</h2>
            {items.map(order => {
              const details = describe(order);
              return (
                <button key={`${order.type}-${order.id}`} onClick={() => navigate(`/order/${order.type}/${order.id}`)} style={{ width: '100%', minHeight: 76, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', border: 0, borderBottom: `1px solid ${colors.line}`, background: 'transparent', textAlign: 'left' }}>
                  <span className="sls-tnum" style={{ minWidth: 88, fontSize: 17, fontWeight: 700 }}>{details.sign}${Number(order.amount).toFixed(2)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: 'block', fontSize: 16, fontWeight: 600 }}>{details.title}</span><span style={{ display: 'block', marginTop: 3, fontSize: 14, color: colors.textMuted }}>{details.subtitle}</span></span>
                  <StatusPill status={statusKind(order.status)} label={t(`status.${order.status}`, order.status)} />
                </button>
              );
            })}
          </section>
        )) : <EmptyState icon="receipt" title={t('profile.transactions.empty')} body={t('profile.transactions.emptyBody')} action={{ label: t('profile.transactions.addFunds'), onPress: () => navigate('/wallet-topup') }} />}
      </div>
    </main>
  );
}
